/**
 * RL-905 + RL-1175: a doctor's public app profile as staff see it
 * (GET/PUT /api/v1/admin/staff/{id}/doctor-profile). Only clinic-approved
 * facts; the patient app shows them on GET /mobile/clinic/doctors/{id}.
 */
export interface DoctorProfile {
  staffId: string;
  name: string;
  /** RL-1790/1791: kept by the clinic manager on Doctors & rosters. */
  email?: string;
  mmcNumber: string | null;
  /** YYYY-MM-DD; no current APC = cannot sign prescriptions or issue MCs. */
  apcValidUntil?: string | null;
  /** Send back on PATCH doctor-details (409 DOCTOR_DETAILS_CHANGED if stale). */
  detailsVersion?: string;
  /** Approved qualification lines in display order (≤ 10, 2–120 chars, unique). */
  qualifications: string[];
  /** https link of the approved photo; null = no photo. */
  photoUrl: string | null;
  /** configured = at least one qualification line or a photo. */
  profileState: 'configured' | 'not_configured';
  /** null while the profile was never configured. */
  updatedAt: string | null;
  updatedBy: { id: string; name: string } | null;
}

/** PUT body: replaces both fields. */
export interface PutDoctorProfileInput {
  qualifications: string[];
  /** The `updatedAt` read (null when never configured); 409 DOCTOR_PROFILE_CHANGED if stale. */
  updatedAt?: string | null;
}

/** Limits from PutDoctorProfileDto (RL-905). */
export const DOCTOR_PROFILE_LIMITS = {
  maxLines: 10,
  minLineLength: 2,
  maxLineLength: 120,
} as const;

/** Read: `staff:read` or `doctor_profiles:read`; write: `staff:manage` or `doctor_profiles:manage`. */
export const DOCTOR_PROFILE_PERMISSIONS = {
  read: ['staff:read', 'doctor_profiles:read'],
  manage: ['staff:manage', 'doctor_profiles:manage'],
} as const;

/** PATCH /admin/staff/{id}/doctor-details: only sent fields change (RL-1790). */
export interface PatchDoctorDetailsInput {
  fullName?: string;
  email?: string;
  mmcNumber?: string | null;
  apcValidUntil?: string | null;
  detailsVersion?: string;
}
